import React, { useContext, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  Plus,
} from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { AuthContext } from "../../contexts/AuthContext";
import {
  getAttendanceRecords,
  getAttendanceSummary,
} from "../../services/attendance";
import { getTeacherClasses } from "../../services/classes";
import { getStudents } from "../../services/students";
import { useHorizontalDragScroll } from "../../hooks/useHorizontalDragScroll";

const TeacherAttendance: React.FC = () => {
  const tableScrollRef = useHorizontalDragScroll<HTMLDivElement>();
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectedClass, setSelectedClass] = useState("");
  const [view, setView] = useState<"today" | "summary" | "records">("today");

  // Fetch attendance summary for selected date
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ["attendance-summary", selectedDate],
    queryFn: () => getAttendanceSummary(selectedDate, "student"),
    enabled: !!selectedDate,
  });

  // Fetch attendance records
  const { data: recordsData, isLoading: recordsLoading } = useQuery({
    queryKey: ["attendance-records", selectedDate, selectedClass],
    queryFn: () =>
      getAttendanceRecords({
        dateFrom: selectedDate,
        dateTo: selectedDate,
        userType: "student",
        ...(selectedClass && { classId: selectedClass }),
      }),
    enabled: !!selectedDate,
  });

  // Fetch classes for the current teacher
  const { data: classesData, error: classesError } = useQuery({
    queryKey: ["teacher-classes"],
    queryFn: () => getTeacherClasses(),
    enabled: !!user,
  });

  // Fetch students for the selected class
  const { data: studentsData } = useQuery({
    queryKey: ["class-students", selectedClass],
    queryFn: () => getStudents({ classId: selectedClass }),
    enabled: !!selectedClass,
  });

  // Use classes data directly since getTeacherClasses returns only teacher's classes
  const teacherClasses = useMemo(() => {
    return classesData?.data || [];
  }, [classesData]);

  if (summaryLoading || recordsLoading) {
    return <LoadingSpinner />;
  }

  const summary = summaryData?.data;
  const records = recordsData?.data || [];
  const recordsSummary = (recordsData as any)?.summary; // Get summary from records response
  const students = studentsData?.data || [];

  // Use records summary if available, otherwise use summary data
  const activeSummary = recordsSummary || summary;

  const attendanceStats = {
    totalPresent: (activeSummary as any)?.present || 0,
    totalAbsent: (activeSummary as any)?.absent || 0,
    totalLate: (activeSummary as any)?.late || 0,
    attendanceRate: activeSummary
      ? ((activeSummary as any)?.present /
          ((activeSummary as any)?.total || 1)) *
        100
      : (summary as any)?.attendancePercentage || 0,
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          Attendance Management
        </h1>
        <p className="text-gray-600 mt-2">
          Track and manage student attendance
        </p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Date Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Class Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Class
              </label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={!teacherClasses.length}
              >
                <option value="">
                  {classesError
                    ? "Error loading classes"
                    : teacherClasses.length
                    ? "All Classes"
                    : "No classes assigned"}
                </option>
                {teacherClasses.map((cls: any) => (
                  <option key={cls._id} value={cls._id}>
                    {cls.name}
                  </option>
                ))}
              </select>
              {classesError && (
                <p className="text-sm text-red-600 mt-1">
                  Failed to load classes. Please refresh the page.
                </p>
              )}
            </div>

            {/* View Toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                View
              </label>
              <div className="flex rounded-md  gap-4">
                <button
                  onClick={() => setView("today")}
                  className={`px-3 py-2 text-sm font-medium rounded-l-md ${
                    view === "today"
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Today
                </button>
                <button
                  onClick={() => setView("summary")}
                  className={`px-3 py-2 text-sm font-medium ${
                    view === "summary"
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Summary
                </button>
                <button
                  onClick={() => setView("records")}
                  className={`px-3 py-2 text-sm font-medium rounded-r-md ${
                    view === "records"
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Records
                </button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 transition-colors">
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Present</p>
              <p className="text-2xl font-bold text-gray-900">
                {attendanceStats.totalPresent}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <XCircle className="h-8 w-8 text-red-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Absent</p>
              <p className="text-2xl font-bold text-gray-900">
                {attendanceStats.totalAbsent}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Late</p>
              <p className="text-2xl font-bold text-gray-900">
                {attendanceStats.totalLate}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Attendance Rate
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {attendanceStats.attendanceRate.toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content based on view */}
      {view === "today" && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Today's Attendance
            </h2>
          </div>
          <div className="p-6">
            {selectedClass ? (
              <div className="space-y-4">
                {students.length > 0 ? (
                  students.map((student: any) => {
                    const attendanceRecord = records.find((record: any) => {
                      // Handle userId being either string or object
                      const recordUserId =
                        typeof record.userId === "string"
                          ? record.userId
                          : record.userId?._id;
                      return recordUserId === student.userId;
                    });

                    // Handle clock in time field names
                    const clockInTime =
                      (attendanceRecord as any)?.clockInTime ||
                      (attendanceRecord as any)?.clockIn;

                    return (
                      <div
                        key={student._id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden relative">
                            {(student.photoUrl ||
                              student.userInfo?.profileDetails
                                ?.profilePicture) ? (
                              <img
                                src={
                                  student.photoUrl ||
                                  student.userInfo?.profileDetails
                                    ?.profilePicture
                                }
                                alt={`${student.userInfo?.firstName || student.firstName} ${student.userInfo?.lastName || student.lastName}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  const fallback =
                                    e.currentTarget.nextElementSibling;
                                  if (fallback instanceof HTMLElement) {
                                    fallback.classList.remove("hidden");
                                  }
                                }}
                              />
                            ) : null}
                            <div
                              className={`${
                                student.photoUrl ||
                                student.userInfo?.profileDetails?.profilePicture
                                  ? "hidden"
                                  : ""
                              } absolute inset-0 bg-blue-100 flex items-center justify-center`}
                            >
                              <span className="text-blue-600 font-medium text-sm">
                                {(
                                  student.userInfo?.firstName ||
                                  student.firstName ||
                                  "S"
                                )?.charAt(0)}
                                {(
                                  student.userInfo?.lastName ||
                                  student.lastName ||
                                  "S"
                                )?.charAt(0)}
                              </span>
                            </div>
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">
                              {student.userInfo?.firstName || student.firstName}{" "}
                              {student.userInfo?.lastName || student.lastName}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {student.studentId}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span
                            className={`px-3 py-1 text-sm font-medium rounded-full ${
                              attendanceRecord?.status === "present"
                                ? "bg-green-100 text-green-800"
                                : attendanceRecord?.status === "late"
                                ? "bg-yellow-100 text-yellow-800"
                                : attendanceRecord?.status === "absent"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {attendanceRecord?.status || "Not Marked"}
                          </span>
                          {clockInTime && (
                            <span className="text-sm text-gray-500">
                              {(attendanceRecord as any)?.formattedClockIn ||
                                new Date(clockInTime).toLocaleTimeString()}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-500 text-center py-8">
                    No students found for this class.
                  </p>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                Please select a class to view attendance.
              </p>
            )}
          </div>
        </div>
      )}

      {view === "summary" && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Attendance Summary for {selectedDate}
          </h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="font-medium">Total Students:</span>
              <span>
                {attendanceStats.totalPresent +
                  attendanceStats.totalAbsent +
                  attendanceStats.totalLate}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="font-medium">Present:</span>
              <span className="text-green-600">
                {attendanceStats.totalPresent}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="font-medium">Absent:</span>
              <span className="text-red-600">
                {attendanceStats.totalAbsent}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="font-medium">Late:</span>
              <span className="text-yellow-600">
                {attendanceStats.totalLate}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="font-medium">Attendance Rate:</span>
              <span className="font-bold text-blue-600">
                {attendanceStats.attendanceRate.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      )}

      {view === "records" && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Attendance Records
            </h2>
          </div>
          <div ref={tableScrollRef} className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Clock In
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Clock Out
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hours
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {records.length > 0 ? (
                  records.map((record: any) => {
                    // Handle userId being either a string or populated object
                    const userName =
                      record.userId?.fullName ||
                      `${record.userId?.firstName || ""} ${
                        record.userId?.lastName || ""
                      }`.trim() ||
                      record.userId;

                    // Handle different clock time field names
                    const clockInTime = record.clockInTime || record.clockIn;
                    const clockOutTime = record.clockOutTime || record.clockOut;

                    return (
                      <tr key={record._id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {userName}
                            </div>
                            {record.studentId && (
                              <div className="text-sm text-gray-500">
                                {record.studentId.admissionNumber}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              record.status === "present"
                                ? "bg-green-100 text-green-800"
                                : record.status === "late"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {record.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {clockInTime
                            ? record.formattedClockIn ||
                              new Date(clockInTime).toLocaleTimeString()
                            : "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {clockOutTime
                            ? record.formattedClockOut ||
                              new Date(clockOutTime).toLocaleTimeString()
                            : "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.durationMinutes
                            ? `${Math.floor(record.durationMinutes / 60)}h ${
                                record.durationMinutes % 60
                              }m`
                            : record.totalHours
                            ? `${record.totalHours.toFixed(1)}h`
                            : "-"}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-4 text-center text-gray-500"
                    >
                      No attendance records found for the selected criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherAttendance;
